import { useEffect, useEffectEvent, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
/**
 * @abstract Hook to transform a BehaviorSubject into a stateful value that can be used in a React component.
 *
 * @example
 * ```ts
 * const [state$, dispatch] = useObservableReducer(reducer, initialState);
 * const foo = useObservableValue(state$, state => state.foo.bar);
 * ```
 *
 * @param observable$ `BehaviorSubject<T>`: A BehaviorSubject of T
 * @param selector `(inner: T) => P`: A selector function that receives T and returns any P
 * @returns `P`: Returns the result of `selector`
 */
export const useObservableValue = (...args) => {
    const [value, setValue] = useState(() => withInitialValue(args));
    const [observable$, selector] = args;
    // Re-subscribing to a BehaviorSubject re-emits its value synchronously, so keying the subscription
    // on an inline selector would loop subscribe -> setValue -> re-render; read it via an effect event.
    const onNext = useEffectEvent((inner) => setValue(selector(inner)));
    useEffect(() => {
        const subscription = observable$.subscribe(onNext);
        return () => subscription.unsubscribe();
    }, [observable$]);
    return value;
};
const withInitialValue = (args) => {
    if (args[0] instanceof BehaviorSubject) {
        return args[1](args[0].value);
    }
    return args[2];
};
